import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "_layout-maxWidth" }

import {
  computed,
  inject, onMounted, provide, reactive, ref, watch,
} from 'vue';
import { invokeKey, navigateKey } from '@drapejs/core';
import { commands as mediaCommands } from '../commands/media';

import {
  MEDIA_FILTERING_QUERY_PARAMETER,
  MEDIA_FILTERING_QUERY_FILTER_GROUP_SEPARATOR,
  MEDIA_FILTERING_QUERY_FILTER_KEY_SEPARATOR,
  MEDIA_FILTERING_QUERY_FILTER_VALUES_SEPARATOR,
  MEDIA_PAGING_QUERY_PARAMETER,
} from '../media-constants';
import MediaPage from '@/components/Media/MediaPage.vue';
import { frameworkContentKey, litiumKey } from '@/keys';
import useMeta from '@/composables/useMeta';


export default /*#__PURE__*/_defineComponent({
  __name: 'Media',
  setup(__props) {

function getCurrentUrl() {
  return window.document.location.href;
}

const invoke = inject(invokeKey);

function getFilterObj() {
  return decodeURIComponent(new URLSearchParams(document.location.search).get(MEDIA_FILTERING_QUERY_PARAMETER) || '')
    .split(MEDIA_FILTERING_QUERY_FILTER_GROUP_SEPARATOR).map((filter) => {
      if (filter.includes(MEDIA_FILTERING_QUERY_FILTER_KEY_SEPARATOR)) {
        const [filterKey, filterValues] = filter.split(MEDIA_FILTERING_QUERY_FILTER_KEY_SEPARATOR);
        return {
          [filterKey]: filterValues.split(MEDIA_FILTERING_QUERY_FILTER_VALUES_SEPARATOR),
        };
      }
      return {};
    }).reduce((obj, item) => {
      Object.assign(obj, item);
      return obj;
    }, {}) || {};
}

function getPage() {
  return new URLSearchParams(document.location.search).get(MEDIA_PAGING_QUERY_PARAMETER) || '1';
}

const setAwaitingUpdates = ref(0);

const mediaState = reactive({
  groups: [] as any[],
  filters: [] as any[],
  page: 0,
  totalPages: 0,
  awaitingUpdates: 0,
});

function setMedia(mediaEntries) {
  mediaState.groups = mediaEntries.groups;
  mediaState.filters = mediaEntries.filters;
  mediaState.page = mediaEntries.page;
  mediaState.totalPages = mediaEntries.totalPages;
}

function appendMedia(mediaEntries) {
  mediaState.groups.push(...mediaEntries.groups);
  mediaState.filters = mediaEntries.filters;
  mediaState.page = mediaEntries.page;
  mediaState.totalPages = mediaEntries.totalPages;
}

async function refreshMedia() {
  try {
    setAwaitingUpdates.value += 1;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const response = await invoke!(mediaCommands.refreshMedia, {
      url: getCurrentUrl(),
      filters: JSON.stringify(getFilterObj()),
      searchQuery: sessionStorage.getItem('media_searchQuery') || '',
      page: getPage(),
    });

    setMedia(response);
  } finally {
    setAwaitingUpdates.value -= 1;
  }
}

async function loadMoreMedia(pageToLoad) {
  try {
    setAwaitingUpdates.value += 1;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const response = await invoke!(mediaCommands.refreshMedia, {
      url: getCurrentUrl(),
      filters: JSON.stringify(getFilterObj()),
      searchQuery: sessionStorage.getItem('media_searchQuery') || '',
      page: pageToLoad,
    });

    const lastOldGroup = mediaState.groups[mediaState.groups.length - 1];
    const nextNewGroup = response.groups[0];

    if (lastOldGroup.groupTitle === nextNewGroup.groupTitle) {
      response.groups.shift();

      const lastOldSubGroup = lastOldGroup.subGroups[lastOldGroup.subGroups.length - 1];
      const firstNewSubGroup = nextNewGroup.subGroups[0];

      if (lastOldSubGroup.subGroupTitle === firstNewSubGroup.subGroupTitle) {
        nextNewGroup.subGroups.shift();

        lastOldSubGroup.folders.push(...firstNewSubGroup.folders);
      }

      lastOldGroup.subGroups.push(...nextNewGroup.subGroups);
    }
    appendMedia(response);
  } finally {
    setAwaitingUpdates.value -= 1;
  }
}

const litium = inject(litiumKey);
const isLoggedIn = computed(() => litium?.value?.uid && litium?.value?.uid !== 'NotAuthenticated');
const navigate = inject(navigateKey);

const frameworkContent = inject(frameworkContentKey);

watch(isLoggedIn,
  (didLogin) => {
    const materialBankLoginPagePath = frameworkContent?.value.MaterialBankLoginPage?.href;
    if (!didLogin && materialBankLoginPagePath) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      navigate!(materialBankLoginPagePath);
    }
  }, { immediate: true });

useMeta();

onMounted(() => {
  refreshMedia();
});

provide('mediabank', {
  state: mediaState,
  refreshMedia,
  loadMoreMedia,
  isLoggedIn,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(MediaPage)
  ]))
}
}

})